import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import interactionPlugin from "@fullcalendar/interaction";
import momentPlugin from "@fullcalendar/moment";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timeGridPlugin from "@fullcalendar/timegrid";

import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Flex from "astrid-components/lib/components/Layout/Flex";
import productionStatuses from "astrid-firestore/src/api/productions/constants/productionStatuses";

import themeColors from "../../../ui/constants/themeColors";

export default function ProductionsCalendarView({ data }) {
	const { i18n, t } = useTranslation();
	const navigate = useNavigate();
	const ref = useRef();

	if (!data) {
		return;
	}

	const resources = data.map(({ id, title, ...production }) => ({
		id,
		title,
		...production,
	}));

	const events = resources.flatMap((production) => {
		const resourceId = production.id;

		const statuses = [productionStatuses.ACCEPTED, productionStatuses.PLANNING, productionStatuses.PRODUCTION];

		const productionStart = production.statusTimeline?.find(({ status }) => statuses.includes(status))?.timestamp;

		const dateCreated = production.created ? new Date(production.created) : null;
		const dateAccepted = productionStart ? new Date(productionStart._seconds * 1000) : null;
		const dateProduction = production.productionDate ? new Date(production.productionDate) : null;
		const dateDelivery = production.deliveryDate ? new Date(production.deliveryDate) : null;

		const created = `${t("created", "Created")} ${dateCreated?.toLocaleDateString(undefined, {
			month: "numeric",
			day: "numeric",
		})}`;

		const due = `${t("due", "Due")} ${dateProduction?.toLocaleDateString(undefined, {
			month: "numeric",
			day: "numeric",
		})}`;

		const delivery = `${t("delivery", "Delivery")} ${dateDelivery?.toLocaleDateString(undefined, {
			month: "numeric",
			day: "numeric",
		})}`;

		return [
			{
				resourceId,
				title: created,
				start: dateCreated,
				end: dateAccepted ? new Date(dateAccepted.getTime() - 3600 * 24 * 1000) : null,
				backgroundColor: themeColors.GREY_FADED,
				borderColor: themeColors.GREY_FADED,
				textColor: themeColors.GREY,
				textAlign: "left",
			},
			{
				resourceId,
				title: due,
				start: dateAccepted,
				end: dateProduction,
				backgroundColor: themeColors.BLUE_FADED,
				borderColor: themeColors.BLUE_FADED,
				textColor: themeColors.BLUE,
			},
			{
				resourceId,
				title: delivery,
				start: dateProduction ? new Date(dateProduction.getTime() + 3600 * 24 * 1000) : null,
				end: dateDelivery,
				backgroundColor: themeColors.GREEN_FADED,
				borderColor: themeColors.GREEN_FADED,
				textColor: themeColors.GREEN,
			},
		].filter((event) => !!event.start && !!event.end && event.start < event.end);
	});

	return (
		<div style={{ marginTop: 13 }}>
			<FullCalendar
				ref={ref}
				schedulerLicenseKey="0520726615-fcs-1698239718"
				plugins={[momentPlugin, resourceTimelinePlugin, interactionPlugin, timeGridPlugin]}
				initialView={"resourceTimelineYear"}
				height="70vh"
				locales={[enLocale, svLocale]}
				locale={i18n.language}
				firstDay={1}
				resources={resources}
				resourceOrder="name"
				resourceAreaWidth="20%"
				events={events}
				eventContent={({ event }) => {
					const textAlign = event.extendedProps.textAlign === "left" ? "flex-start" : "flex-end";

					return (
						<Flex justifyContent={textAlign} style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
							{event.title}
						</Flex>
					);
				}}
				eventClick={({ event }) => {
					const resource = event.getResources()[0];

					navigate(`/production/${resource.id}`);
				}}
				eventOverlap={false}
				displayEventTime={false}
				slotLabelFormat={[
					"MMMM",
					{
						day: "numeric",
					},
				]}
				slotLabelContent={({ text }) => <div style={{ color: themeColors.TEXT }}>{text}</div>}
				scrollTime={{ months: new Date().getMonth(), day: new Date().getDate() - 1 }}
				scrollTimeReset={false}
				headerToolbar={false}
			/>
		</div>
	);
}
